/**
 * WHETHER A TEXT WOULD GO PAST OPS'S OWN SWITCH, AND THE LINE THAT SAYS WHERE IT STANDS (ops #31).
 *
 * Ops holds a go-live switch for texts, off until an owner moves it on the Settings screen, on the
 * say-so recorded in FaithfullyKept/faithfully-kept-phone#16: off, test handsets only, or on.
 * `GET /api/me` tells the office where it stands (`me.smsGoLive`), and the two places a person
 * composes a text, the move flow and "Send the invoice", ask here whether a text to one number would
 * go: yes while it is on, or at test handsets for a number on the list. Where it would not, the text
 * is offered disabled with the switch named, and email and the typed reason stay. The server asks the
 * switch again at every text it composes and parks one it refuses, so this is UX and never the
 * control (binding rule 6).
 *
 * The phone system has a switch of its own, and its newest word that texting is not enabled there is
 * drawn beneath the line while ops is not on: during a test it is the other half of why a text did
 * not arrive. Once ops is on there is no line, and a text that fails says why on its own record.
 */
import type { SmsGoLive } from '../api.ts';
import { SMS_GO_LIVE_LINES } from '../labels.ts';

export interface TextingPath {
  /** Whether a text to this number would go past ops's switch. */
  readonly open: boolean;
  /** Where the switch stands, as the one line; null once it is on. */
  readonly line: string | null;
  /** The phone system's newest word that texting is not enabled there, beneath the line, or null. */
  readonly phoneSaid: string | null;
  /** Where a text would not go, the sentence naming the switch; null where it would. */
  readonly why: string | null;
}

const OFF_WHY = 'No text goes from ops until an owner moves the texting switch in Settings.';
const NOT_LISTED_WHY =
  'Ops texts only the test handsets listed beside the texting switch in Settings, and their number is not one of them.';

/**
 * The switch as it applies to one number, or to none yet. Absent reads as off, the safe direction:
 * the office is always told, and a screen that was not must not offer a text the server would park.
 */
export function textingPath(
  view: SmsGoLive | null | undefined,
  number: string | null,
): TextingPath {
  const state = view?.state ?? 'off';
  const listed = number !== null && (view?.handsets ?? []).includes(number);
  const open = state === 'on' || (state === 'test_handsets' && listed);
  const line = SMS_GO_LIVE_LINES[state];
  return {
    open,
    line,
    phoneSaid: line === null ? null : (view?.phoneSystemSaid ?? null),
    why: open ? null : state === 'test_handsets' ? NOT_LISTED_WHY : OFF_WHY,
  };
}

/**
 * The line, and where a text would not go, why not and the way on, in the screen's own words; the
 * phone system's newest word beneath. Nothing at all once texting is on.
 */
export function TextingGate({ path, wayOn }: { path: TextingPath; wayOn: string }) {
  if (path.line === null) return null;
  return (
    <div
      id="texting-gate"
      className={`mb-3 rounded-lg border p-3 ${
        path.open ? 'border-navy/20' : 'border-coral/50 border-l-4 border-l-coral bg-parchment'
      }`}
    >
      <p className="text-sm font-medium">{path.line}.</p>
      {path.why ? (
        <p className="mt-1 text-sm">
          {path.why} {wayOn}
        </p>
      ) : null}
      {path.phoneSaid ? (
        <p className="mt-1 text-xs text-navy/65">The phone system last said: {path.phoneSaid}</p>
      ) : null}
    </div>
  );
}
